import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { MUSIC24_TRIAL_DAYS } from 'fym-shared/src/music24/music24Plans';
import { upsellShowsHomeCard } from 'fym-shared/src/music24/watermarkUpsell';
import { TypedMemo } from '../../../common/types';
import { useUserTokenDecoded } from '../../../../redux/slices/appInfo/hooks';
import { SMLButton } from '../../components/primitives/SMLButton';
import { watermarkChoiceMessages } from '../choice/watermarkChoiceMessages';
import { watermarkFileMessages } from '../files/watermarkFileMessages';
import { WatermarkThumbTile } from '../files/WatermarkThumbTile';
import { useOpenWatermarkOffer } from '../useOpenWatermarkOffer';
import { useUpsellEventFacts } from '../useUpsellEventFacts';
import { useUpsellFacts } from '../useUpsellFacts';
import { useUpsellShown } from '../useUpsellShown';
import { useWatermarkSummary } from '../useWatermarkSummary';
import { trackUpsellDismissed } from '../watermarkUpsellAnalytics';
import { isHomeCardSnoozed, snoozeHomeCard } from './homeCardSnooze';
import { upsellTrialPhase } from './trialPhase';
import { watermarkTrialMessages as messages } from './watermarkTrialMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the upsell; each part uses its own slice of it */
import styles from '../WatermarkUpsell.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The card draws this many thumbnails (design b2). */
const CARD_THUMBS = 3;

/**
 * The first card of the artist Home (design: trial-upsell b2): from day 3 of the trial with 3 or
 * more watermarked files, and on Free (`upsellShowsHomeCard`). Only the owner sees it; the demo
 * artist never. "Later" hides it for 48 hours on this device; on the last day and on Free it comes
 * back with the texts of the pill (b1).
 */
const WatermarkHomeCardComponent: React.FC = () => {
  const intl = useIntl();
  const { facts, isReady } = useUpsellFacts();
  const { summary } = useWatermarkSummary();
  const userId = useUserTokenDecoded()?.id ?? null;
  const openOffer = useOpenWatermarkOffer();
  const eventFacts = useUpsellEventFacts();
  const phase = upsellTrialPhase(facts.plan, facts.trialDaysRemaining);
  const [hidden, setHidden] = useState(false);
  const shows =
    isReady &&
    !hidden &&
    upsellShowsHomeCard(facts, MUSIC24_TRIAL_DAYS) &&
    !isHomeCardSnoozed(userId, phase, Date.now());
  useUpsellShown('home_card', shows);
  const thumbs = useMemo(() => summary.thumbs.slice(0, CARD_THUMBS), [summary.thumbs]);
  const remove = useCallback(() => openOffer('home_card'), [openOffer]);
  const later = useCallback(() => {
    snoozeHomeCard(userId, phase, Date.now());
    trackUpsellDismissed(eventFacts('home_card'));
    setHidden(true);
  }, [eventFacts, phase, userId]);

  if (!shows) {
    return null;
  }
  const count = summary.files;
  let eyebrow = intl.formatMessage(watermarkFileMessages.onFree);
  let body = intl.formatMessage(messages.pillFree, { count });
  if (phase === 'lastDay') {
    eyebrow = intl.formatMessage(messages.endsTomorrow);
    body = intl.formatMessage(messages.pillLastDay, { count });
  } else if (phase === 'running') {
    eyebrow = intl.formatMessage(watermarkFileMessages.daysLeft, { days: facts.trialDaysRemaining ?? 0 });
    body = summary.cleanCopiesAvailable
      ? intl.formatMessage(messages.cardBody, { count })
      : intl.formatMessage(watermarkFileMessages.bannerBodyNoCopies);
  }

  return (
    <section className={styles.card} data-testid="watermark-home-card">
      <div className={styles.strip} aria-hidden="true">
        {thumbs.map((thumb) => (
          <WatermarkThumbTile key={thumb.url} url={thumb.url} shape={thumb.shape} />
        ))}
      </div>
      <div>
        <div className={clsx(styles.eyebrow, phase === 'lastDay' && styles.eyebrowWarm)}>{eyebrow}</div>
        <h3 className={clsx(styles.h, styles.hSmall, 'mt-2')}>
          {summary.songTitle === null
            ? intl.formatMessage(messages.cardTitleNoSong)
            : intl.formatMessage(messages.cardTitle, { song: summary.songTitle })}
        </h3>
        <p className={styles.p}>{body}</p>
      </div>
      <div className={styles.cardActs}>
        <SMLButton variant="primary" className={styles.stretch} onClick={remove}>
          {intl.formatMessage(watermarkChoiceMessages.remove)}
        </SMLButton>
        <button type="button" className={clsx(styles.later, styles.laterCenter)} onClick={later}>
          {intl.formatMessage(messages.later)}
        </button>
      </div>
    </section>
  );
};

export const WatermarkHomeCard = TypedMemo(WatermarkHomeCardComponent);
